import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  RadioGroup,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import * as React from "react";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";

import { useAnalytics, useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { layoutQueryKeys } from "@/shared/domain/layouts/hooks";
import {
  createLayoutFormSchema,
  CreateLayoutFormFields,
} from "@/shared/domain/layouts/LayoutFormModel";
import {
  CreateLayoutRequest,
  LayoutAccessibility,
  LayoutRecord,
} from "@/shared/domain/layouts/LayoutRecord";
import { PaginatedResponse, RobotoDomainException } from "@/shared/http";
import { ErrorMonitoringService, LoggerService } from "@/shared/services";

import { useGetWorkingStateAsLayout } from "../../layouts/hooks/useGetWorkingStateAsLayout";

interface CreateLayoutFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (layout: LayoutRecord) => void;
}

export function CreateLayoutFormModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateLayoutFormModalProps) {
  const { trackEvent } = useAnalytics();
  const form = useForm<CreateLayoutFormFields>({
    defaultValues: {
      name: "",
      accessibility: LayoutAccessibility.User,
      tags: [],
    },
    mode: "onBlur",
    resolver: zodResolver(
      createLayoutFormSchema,
    ) as Resolver<CreateLayoutFormFields>,
  });

  const getWorkingStateAsLayout = useGetWorkingStateAsLayout();
  const createLayout = useCreateLayout();

  const serverError = form.formState.errors.root?.serverErrors?.message;

  const onSubmit: SubmitHandler<CreateLayoutFormFields> = (data) => {
    const { layout_definition, schema_version } = getWorkingStateAsLayout();

    LoggerService.log("Creating layout:", {
      ...data,
      layout_definition,
      schema_version,
    });

    return createLayout
      .mutateAsync({
        ...data,
        layout_definition,
        schema_version,
      })
      .then(onSuccess);
  };

  // Clears the form fields when the modal is opened
  React.useEffect(() => {
    if (isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        trackEvent("CreateLayoutCanceled");
        onClose();
      }}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
    >
      <form
        onSubmit={(event) => {
          form
            .handleSubmit(onSubmit)(event)
            .catch((e) => {
              LoggerService.error("Failed to create layout:", e);

              let errorMessage = "Failed to create layout. Please try again.";

              // If it's a client error, we can show the user the message
              if (e instanceof RobotoDomainException && e.isClientError()) {
                errorMessage = e.message;
              }

              form.setError("root.serverErrors", {
                message: errorMessage,
              });
            });
        }}
      >
        <DialogTitle id="form-dialog-title">
          Create a layout from your current workspace
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <div>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Layouts help you organize and save your workspace configuration,
                including:
              </Typography>
              <Typography component="ul" sx={{ pl: 2, mb: 2 }}>
                <li>Panel arrangements and sizes</li>
                <li>Data visualizations and settings</li>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can reuse layouts across different datasets or share them
                with your team.
              </Typography>
            </div>

            {serverError && <Alert severity="error">{serverError}</Alert>}

            <div>
              <Typography
                component="label"
                htmlFor="name"
                sx={{ display: "block", mb: 1 }}
              >
                Layout name
              </Typography>
              <TextField
                {...form.register("name")}
                id="name"
                placeholder="e.g. Motor Analysis"
                fullWidth
                autoFocus
                size="small"
                error={!!form.formState.errors.name}
                helperText={form.formState.errors.name?.message}
              />
            </div>

            <div>
              <Typography component="label" sx={{ display: "block", mb: 1 }}>
                Layout visibility
              </Typography>
              <Controller
                control={form.control}
                name="accessibility"
                render={({ field, fieldState: { error } }) => (
                  <>
                    <RadioGroup
                      {...field}
                      defaultValue={LayoutAccessibility.User}
                      row
                      sx={{ gap: 2 }}
                    >
                      <label>
                        <Radio value={LayoutAccessibility.User} />
                        Personal
                      </label>
                      <label>
                        <Radio value={LayoutAccessibility.Organization} />
                        Team
                      </label>
                    </RadioGroup>
                    {error && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: 0.5 }}
                      >
                        {error.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </div>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <RobotoButton
            eventName="CreateLayoutCanceled"
            onClick={onClose}
            type="button"
            disabled={createLayout.isPending}
          >
            Cancel
          </RobotoButton>
          <RobotoButton
            eventName="CreateLayoutClicked"
            eventProperties={{
              name: form.watch("name"),
              accessibility: form.watch("accessibility"),
            }}
            type="submit"
            variant="contained"
            disabled={createLayout.isPending}
            endIcon={
              createLayout.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {createLayout.isPending ? "Creating..." : "Create Layout"}
          </RobotoButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function useCreateLayout() {
  const { layoutService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateLayoutRequest) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }

      return layoutService.createLayout(request, currentOrganization.org_id);
    },
    onError: (error) => {
      ErrorMonitoringService.captureError(error);
      LoggerService.error("Failed to create layout:", error);
    },
    onSuccess: (layoutRecord: LayoutRecord) => {
      return queryClient.setQueryData<PaginatedResponse<LayoutRecord>>(
        layoutQueryKeys.lists(),
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          return {
            ...oldData,
            items: [...oldData.items, layoutRecord],
          };
        },
      );
    },
  });
}
